'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Cookie, X, Check } from 'lucide-react';

const CONSENT_KEY = 'griott_cookie_consent';

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      // Small delay so it doesn't flash immediately on load
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const accept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    setVisible(false);
  };

  const decline = () => {
    localStorage.setItem(CONSENT_KEY, 'declined');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[100] p-4 sm:p-6"
      role="dialog"
      aria-label="Cookie consent"
    >
      <div
        className="max-w-3xl mx-auto rounded-xl border shadow-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: '#E05A4718', color: 'var(--primary)' }}
        >
          <Cookie size={18} />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--foreground)' }}>
            We use cookies
          </p>
          <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
            We use essential cookies for authentication and optional analytics cookies to improve the archive. See our{' '}
            <Link href="/privacy-policy" className="underline" style={{ color: 'var(--primary)' }}>
              Privacy Policy
            </Link>{' '}
            for details.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={decline}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all duration-150"
            style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--muted)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
          >
            <X size={13} />
            Decline
          </button>
          <button
            onClick={accept}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all duration-150 active:scale-95"
            style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
          >
            <Check size={13} />
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
